import {
    Component,
    Output,
    EventEmitter,
    HostBinding,
    ViewEncapsulation,
    ViewChild
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { Observable, BehaviorSubject } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { LevelToolComponent } from '../level-tool/level-tool.component';
import { CreepsService } from '../creeps.service';

import { AuthService, ConstantsService } from '../../core/upgraded-providers';

@Component({
    selector: 'app-power-creep-tool',
    templateUrl: './creep-tool.component.pug',
    styleUrls: ['./creep-tool.component.less'],
    encapsulation: ViewEncapsulation.None
})
export class CreepToolComponent {
    @ViewChild('levelTool', {static: false}) levelTool: LevelToolComponent;

    @Output() save: EventEmitter<any> = new EventEmitter<any>();

    @HostBinding('class.--reset') isConfirmReset = false;
    @HostBinding('class.--confirm') isConfirm = false;
    @HostBinding('class.--confirmed') isConfirmed = false;
    @HostBinding('class.--unconfirmed') isUnconfirmed = false;
    @HostBinding('class.--maximum') get isMaximum() {
        return this.creep && this.creep.level >= this._CONSTANTS.POWER_CREEP_MAX_LEVEL;
    }
    @HostBinding('class.--mistake') get isMistake() {
        return this.mistake;
    }

    get isNew(): boolean {
        return this.creep && !this.creep._id ? true : false;
    }

    get hasChanges(): boolean {
        if (this._historySbj.getValue().length) {
            return true;
        }

        return false;
    }

    private _resolveReset: Function;
    private _rejectReset: Function;

    private _creepsSbj = new BehaviorSubject<any>(null);
    public powerLevel$: Observable<number>;

    private _historySbj = new BehaviorSubject<any[]>([]);
    public history$ = this._historySbj.asObservable();

    public mistake = '';
    public powers = {};

    public creep: IPowerCreep;
    public badge = this._authService.Me.badge;

    constructor(
        private _route: ActivatedRoute,
        private _router: Router,
        private _creepsService: CreepsService,
        private _authService: AuthService,
        private _CONSTANTS: ConstantsService
    ) {
        const creeps = this._route.snapshot.parent.data['creeps'];

        this.powerLevel$ = this._creepsSbj.asObservable()
            .pipe(map((creeps) => creeps.reduce((powerLevel, { level }) => {
                return powerLevel + level + 1;
            }, 0)));

        this._creepsSbj.next([...creeps]);
    }

    public onConfirmReset(confirm) {
        if (confirm) {
            this._resolveReset();
            return;
        }

        this.isConfirmReset = false;
        this._rejectReset();
    }

    public onReset() {
        this.reset().then(() => {
            this.creep = null;
            this.isConfirmReset = false;
            this._historySbj.next([]);
        });
    }

    public reset(): Promise<any> {
        return new Promise((resolve, reject) => {
            if (!this.hasChanges) {
                resolve();
                return;
            }

            if (this.hasChanges && this.isConfirmed) {
                resolve();
                return;
            }

            this.isConfirmReset = true;

            this._resolveReset = () => resolve();
            this._rejectReset = () => reject();
        });
    }

    isOperator(): boolean {
        return this.creep.className === this._CONSTANTS.POWER_CLASS.OPERATOR;
      }

    isCommander(): boolean {
        return this.creep.className === this._CONSTANTS.POWER_CLASS.COMMANDER;
    }

    idExecutor(): boolean {
        return this.creep.className === this._CONSTANTS.POWER_CLASS.EXECUTOR;
    }

    public setCreep(creep: IPowerCreep) {
        this.creep = creep;

        const creeps = this._creepsSbj.getValue();
        const match = creeps.find(({ _id }) => _id === creep._id);

        if (match) {
            const idx = creeps.indexOf(match);
            creeps.splice(idx, 1);
        }

        this._creepsSbj.next([...creeps, creep]);
        this.initPowers();
    }

    public initPowers() {
        // @ts-ignore
        this.powers = Object.entries<IPower>(this._CONSTANTS.POWER_INFO).filter(([, { className }]) => {
            return className === this.creep.className;
        }).sort(([aKey, a], [bKey, b]) => {
            return a.level[0] - b.level[0];
        }).map(([key]) => key);
    }

    public onUndo() {
        const history = this._historySbj.getValue();
        const power = history.pop();

        this.creep.level--;

        this.creep.powers[power] = Object.assign({}, this.creep.powers[power]);
        this.creep.powers[power].level--;

        this._historySbj.next([...history]);

        const creeps = this._creepsSbj.getValue();
        this._creepsSbj.next([...creeps]);
    }

    public onPower(power) {
        this.creep.level++;

        this.creep.powers[power] = Object.assign({}, this.creep.powers[power] || { level: 0 });
        this.creep.powers[power].level++;

        const history = this._historySbj.getValue();
        this._historySbj.next([...history, power]);

        const creeps = this._creepsSbj.getValue();
        this._creepsSbj.next([...creeps]);
    }

    private _create({ name, className }: IPowerCreep) {
        return this._creepsService.create({
            name,
            className
        });
    }

    private _upgrade({ _id: id, powers }: IPowerCreep) {
        powers = Object.entries(powers).reduce((powers, [key, { level }]) => {
            powers[key] = level;
            return powers;
        }, {});

        return this._creepsService.upgrade({
            id,
            powers
        });
    }

    public async onSave() {
        if (this.levelTool.isWarning) {
            this.mistake = 'Your account Global Power Level is not enough.';
            return;
        }

        this.isConfirmReset = false;
        this.isConfirm = true;
        this.isUnconfirmed = false;
    }

    public async onConfirm(confirm) {
        this.isConfirm = false;
        this.isConfirmed = confirm;
        this.isUnconfirmed = !confirm;

        if (!confirm) {
            setTimeout(() => this.isUnconfirmed = null, 1100);

            return;
        }

        const _creep: IPowerCreep = Object.assign({}, this.creep);

        if (!this.creep._id) {
            try {
                const creep = await this._create(_creep);
                delete creep.powers;
                Object.assign(_creep, creep);
            } catch (err) {
                this.mistake = `Server error: ${ err }`;
                this.isConfirmed = false;

                return;
            }
        }

        try {
            await this._upgrade(_creep);
        } catch (err) {
            this.mistake = `Server error: ${ err }`;
            this.isConfirmed = false;

            return;
        }

        this.save.emit();

        setTimeout(() => {
            this._router.navigate(['..'], {
                relativeTo: this._route
            });
        }, 1000);
    }

}
